import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  CreditCard,
  History,
  Bell,
  LogOut,
  Plus,
  Trash2,
  CheckCircle2,
  Building2,
  Menu,
  X
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const UserDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === `/dashboard${path}` || (path === '' && location.pathname === '/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row" data-testid="user-dashboard">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-gray-200 p-4 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center space-x-2">
          <Building2 className="h-6 w-6 text-blue-600" />
          <span className="text-lg font-bold text-gray-900">MassPayHub</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Sidebar - Hidden on mobile unless menu is open */}
      <div className={`
        fixed md:relative inset-0 z-40 md:z-0
        ${mobileMenuOpen ? 'block' : 'hidden md:block'}
      `}>
        {/* Overlay for mobile */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Sidebar content */}
        <div className="fixed md:relative inset-y-0 left-0 w-64 bg-white border-r border-gray-200 flex flex-col z-50">
          <div className="p-4 md:p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Building2 className="h-6 md:h-8 w-6 md:w-8 text-blue-600" />
              <span className="text-lg md:text-xl font-bold text-gray-900">MassPayHub</span>
            </div>
            <p className="text-xs md:text-sm text-gray-600 mt-2 truncate">{user.name}</p>
          </div>

          <nav className="flex-1 p-3 md:p-4 space-y-2 overflow-y-auto">
            <Link
              to="/dashboard"
              onClick={() => setMobileMenuOpen(false)}
              className={`sidebar-link ${isActive('') ? 'active' : ''}`}
              data-testid="nav-overview"
            >
              <LayoutDashboard className="h-5 w-5 mr-3 flex-shrink-0" />
              <span className="text-sm md:text-base">Overview</span>
            </Link>
            <Link
              to="/dashboard/credentials"
              onClick={() => setMobileMenuOpen(false)}
              className={`sidebar-link ${isActive('/credentials') ? 'active' : ''}`}
              data-testid="nav-credentials"
            >
              <CreditCard className="h-5 w-5 mr-3 flex-shrink-0" />
              <span className="text-sm md:text-base">Payment Credentials</span>
            </Link>
            <Link
              to="/dashboard/history"
              onClick={() => setMobileMenuOpen(false)}
              className={`sidebar-link ${isActive('/history') ? 'active' : ''}`}
              data-testid="nav-history"
            >
              <History className="h-5 w-5 mr-3 flex-shrink-0" />
              <span className="text-sm md:text-base">Payment History</span>
            </Link>
            <Link
              to="/dashboard/notifications"
              onClick={() => setMobileMenuOpen(false)}
              className={`sidebar-link ${isActive('/notifications') ? 'active' : ''}`}
              data-testid="nav-notifications"
            >
              <Bell className="h-5 w-5 mr-3 flex-shrink-0" />
              <span className="text-sm md:text-base">Notifications</span>
              {profile?.unread_notifications > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-2 py-0.5 md:py-1 flex-shrink-0">
                  {profile.unread_notifications}
                </span>
              )}
            </Link>
          </nav>

          <div className="p-3 md:p-4 border-t border-gray-200">
            <Button
              variant="outline"
              className="w-full text-sm md:text-base"
              onClick={() => {
                setMobileMenuOpen(false);
                onLogout();
              }}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<Overview profile={profile} />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/history" element={<PaymentHistory />} />
          <Route path="/notifications" element={<Notifications />} />
        </Routes>
      </div>
    </div>
  );
};

const Overview = ({ profile }) => {
  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="overview-section">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Dashboard Overview</h1>

      {/* User Info Card */}
      <div className="data-card mb-6 md:mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg md:text-2xl flex-shrink-0">
            {profile?.user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 truncate">
              {profile?.user?.name || 'User'}
            </h2>
            <p className="text-sm md:text-base text-gray-600 truncate">
              {profile?.user?.email || 'email@example.com'}
            </p>
          </div>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`data-card mb-6 md:mb-8 ${profile?.user?.status === 'pending_approval' ? 'bg-yellow-50 border-yellow-200' : ''}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-semibold text-base md:text-lg">Account Status</h3>
            <p className="text-sm md:text-base text-gray-600 mt-1">
              {profile?.user?.status === 'active' && 'Your account is active and ready to receive payments'}
              {profile?.user?.status === 'pending_approval' && 'Your account is pending admin approval'}
              {profile?.user?.status === 'blocked' && 'Your account has been blocked. Contact support.'}
            </p>
          </div>
          <span className={`status-badge status-${profile?.user?.status} text-xs md:text-sm self-start sm:self-center`} data-testid="account-status">
            {profile?.user?.status}
          </span>
        </div>
      </div>

      {/* Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
        {profile?.accounts?.map((account, idx) => (
          <div key={account.id} className="data-card">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="font-semibold text-base md:text-lg">Account {account.account_number}</h3>
              <span className="text-xs text-gray-500 uppercase font-semibold">{account.currency}</span>
            </div>
            <div className="text-2xl md:text-3xl font-bold text-blue-600" data-testid={`account-${idx}-balance`}>
              ${(account.balance || 0).toFixed(2)} <span className="text-lg md:text-xl">{account.currency}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="data-card">
          <h3 className="text-gray-600 text-xs md:text-sm font-semibold mb-2">Total Payments Received</h3>
          <div className="text-xl md:text-2xl font-bold" data-testid="total-payments">
            ${profile?.total_payments?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-xs md:text-sm font-semibold mb-2">Last Payment Amount</h3>
          <div className="text-xl md:text-2xl font-bold" data-testid="last-payment-amount">
            ${profile?.last_payment_amount?.toFixed(2) || '0.00'}
          </div>
        </div>
      </div>
    </div>
  );
};

const Credentials = () => {
  const [credentials, setCredentials] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    type: 'usdt_trc20',
    details: '',
    label: '',
    // Bank-specific fields
    iban: '',
    beneficiary_name: '',
    bank_name: ''
  });

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await axios.get(`${API}/user/credentials`);
      setCredentials(response.data.credentials);
    } catch (error) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      // Prepare data based on credential type
      let dataToSend = {
        type: formData.type,
        label: formData.label
      };

      if (formData.type === 'bank') {
        // For bank accounts, combine IBAN, beneficiary name, and bank name
        dataToSend.details = JSON.stringify({
          iban: formData.iban,
          beneficiary_name: formData.beneficiary_name,
          bank_name: formData.bank_name
        });
      } else {
        // For crypto wallets, use details as is
        dataToSend.details = formData.details;
      }

      await axios.post(`${API}/user/add-credential`, dataToSend);
      toast.success('Credential added successfully');
      setShowAdd(false);
      setFormData({ type: 'usdt_trc20', details: '', label: '', iban: '', beneficiary_name: '', bank_name: '' });
      fetchCredentials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credential');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) return;
    
    try {
      await axios.delete(`${API}/user/credential/${id}`);
      toast.success('Credential deleted');
      fetchCredentials();
    } catch (error) {
      toast.error('Failed to delete credential');
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await axios.put(`${API}/user/credential/${id}/set-primary`);
      toast.success('Primary credential updated');
      fetchCredentials();
    } catch (error) {
      toast.error('Failed to update primary credential');
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="credentials-section">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Payment Credentials</h1>
        <Button onClick={() => setShowAdd(!showAdd)} data-testid="add-credential-button" className="w-full sm:w-auto text-sm md:text-base">
          <Plus className="h-4 w-4 mr-2" />
          Add Credential
        </Button>
      </div>

      {showAdd && (
        <div className="data-card mb-6 md:mb-8">
          <h3 className="font-semibold text-base md:text-lg mb-4">Add New Credential</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <Label>Credential Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value, details: '', iban: '', beneficiary_name: '', bank_name: '' })}>
                <SelectTrigger data-testid="credential-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="usdt_trc20">USDT (TRC20)</SelectItem>
                  <SelectItem value="usdt_erc20">USDT (ERC20)</SelectItem>
                  <SelectItem value="usdt_bep20">USDT (BEP20)</SelectItem>
                  <SelectItem value="usdc_solana">USDC (Solana)</SelectItem>
                  <SelectItem value="usdc_erc20">USDC (ERC20)</SelectItem>
                  <SelectItem value="usdc_bep20">USDC (BEP20)</SelectItem>
                  <SelectItem value="bank">Bank Account (только для EUR / Only for EUR)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'bank' ? (
              <>
                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={formData.iban}
                    onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                    required
                    placeholder="DE89370400440532013000"
                    data-testid="credential-iban-input"
                  />
                </div>
                <div>
                  <Label>Beneficiary Name (Имя получателя)</Label>
                  <Input
                    value={formData.beneficiary_name}
                    onChange={(e) => setFormData({ ...formData, beneficiary_name: e.target.value })}
                    required
                    placeholder="John Doe"
                    data-testid="credential-beneficiary-input"
                  />
                </div>
                <div>
                  <Label>Bank Name (Название банка)</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    required
                    placeholder="Deutsche Bank"
                    data-testid="credential-bank-input"
                  />
                </div>
              </>
            ) : (
              <div>
                <Label>Wallet Address</Label>
                <Input
                  value={formData.details}
                  onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                  required
                  placeholder="Enter wallet address"
                  data-testid="credential-details-input"
                />
              </div>
            )}

            <div>
              <Label>Label (Optional)</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g., Main Wallet"
                data-testid="credential-label-input"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button type="submit" data-testid="save-credential-button" className="w-full sm:w-auto text-sm md:text-base">Save Credential</Button>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)} className="w-full sm:w-auto text-sm md:text-base">Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : credentials.length === 0 ? (
        <div className="data-card text-center py-12">
          <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No payment credentials added yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {credentials.map((cred) => (
            <div key={cred.id} className="data-card" data-testid="credential-item">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-semibold text-sm md:text-base">{cred.type.replace('_', ' ').toUpperCase()}</span>
                    {cred.is_primary && (
                      <span className="status-badge status-active flex items-center gap-1 text-xs">
                        <CheckCircle2 className="h-3 w-3" />
                        Primary
                      </span>
                    )}
                  </div>
                  {cred.label && <p className="text-xs md:text-sm text-gray-600 mb-1">{cred.label}</p>}
                  
                  {cred.type === 'bank' ? (
                    // Display bank details in structured format
                    (() => {
                      try {
                        const bankDetails = JSON.parse(cred.details);
                        return (
                          <div className="text-xs md:text-sm text-gray-800 space-y-1">
                            <p><span className="font-semibold">IBAN:</span> <span className="font-mono">{bankDetails.iban}</span></p>
                            <p><span className="font-semibold">Beneficiary:</span> {bankDetails.beneficiary_name}</p>
                            <p><span className="font-semibold">Bank:</span> {bankDetails.bank_name}</p>
                          </div>
                        );
                      } catch (e) {
                        return <p className="text-xs md:text-sm text-gray-800 font-mono break-all">{cred.details}</p>;
                      }
                    })()
                  ) : (
                    <p className="text-xs md:text-sm text-gray-800 font-mono break-all">{cred.details}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  {!cred.is_primary && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSetPrimary(cred.id)}
                      data-testid="set-primary-button"
                      className="w-full sm:w-auto text-xs md:text-sm"
                    >
                      Set as Primary
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(cred.id)}
                    className="w-full sm:w-auto text-xs md:text-sm"
                    data-testid="delete-credential-button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PaymentHistory = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/user/payments-history`);
      setPayments(response.data.payments);
    } catch (error) {
      toast.error('Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="payment-history-section">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Payment History</h1>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : payments.length === 0 ? (
        <div className="data-card text-center py-12">
          <History className="h-10 md:h-12 w-10 md:w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm md:text-base text-gray-600">No payment history yet</p>
        </div>
      ) : (
        <div className="data-card overflow-x-auto">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="table-header">
                <th className="text-left p-2 md:p-3 text-xs md:text-sm">Date</th>
                <th className="text-left p-2 md:p-3 text-xs md:text-sm">Amount</th>
                <th className="text-left p-2 md:p-3 text-xs md:text-sm">Currency</th>
                <th className="text-left p-2 md:p-3 text-xs md:text-sm">Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id} className="border-t border-gray-200" data-testid="payment-row">
                  <td className="p-2 md:p-3 text-xs md:text-sm">
                    {new Date(payment.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-2 md:p-3 font-semibold text-xs md:text-sm">${payment.amount.toFixed(2)}</td>
                  <td className="p-2 md:p-3 text-xs md:text-sm">{payment.currency}</td>
                  <td className="p-2 md:p-3">
                    <span className={`status-badge status-${payment.status} text-xs`}>
                      {payment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/user/notifications`);
      setNotifications(response.data.notifications);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/user/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="notifications-section">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Notifications</h1>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="data-card text-center py-12">
          <Bell className="h-10 md:h-12 w-10 md:w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm md:text-base text-gray-600">No notifications</p>
        </div>
      ) : (
        <div className="space-y-3 md:space-y-4">
          {notifications.map((notif) => (
            <div
              key={notif.id}
              className={`data-card cursor-pointer transition-all hover:shadow-md ${!notif.read ? 'border-l-4 border-l-blue-600' : ''}`}
              onClick={() => !notif.read && markAsRead(notif.id)}
              data-testid="notification-item"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm md:text-base mb-1">{notif.title}</h3>
                  <p className="text-xs md:text-sm text-gray-600 break-words">{notif.message}</p>
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
                {!notif.read && (
                  <div className="h-2 w-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
